import { useState } from 'react';
import Layout from '@/components/feature/Layout';

export default function DebtPage() {
  const [codigo, setCodigo] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<{
    nombre: string;
    deuda: number;
    meses: number;
    ultimoPago: string;
    lecturaActual: number;
  } | null>(null);
  const [error, setError] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!codigo.trim()) {
      setError('Ingresa tu código de usuario');
      return;
    }
    setSearching(true);
    // Simulate search - will be connected to real data later
    setTimeout(() => {
      if (codigo === '0000' || codigo === '') {
        setError('Código de usuario no encontrado. Verifica e intenta nuevamente.');
        setSearching(false);
        return;
      }
      // Mock result for demo
      setResult({
        nombre: 'Usuario Demo',
        deuda: 125.50,
        meses: 2,
        ultimoPago: 'Octubre 2025',
        lecturaActual: 245,
      });
      setSearching(false);
    }, 1200);
  };

  return (
    <Layout>
      <div className="bg-primary-900 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-400 mb-3">
            Usuarios
          </span>
          <h1 className="font-heading font-bold text-3xl md:text-5xl text-white mb-4">
            Consultar Deuda
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto">
            Ingresa tu código de usuario para consultar el estado de tu cuenta y deuda actual.
          </p>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="max-w-xl mx-auto">
          {/* Search form */}
          <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70 mb-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Código de Usuario
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    placeholder="Ej: SJ-001234"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all uppercase"
                  />
                  <button
                    type="submit"
                    disabled={searching}
                    className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-semibold rounded-lg transition-colors text-sm whitespace-nowrap flex items-center gap-2"
                  >
                    {searching ? (
                      <>
                        <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                        Buscando...
                      </>
                    ) : (
                      <>
                        <i className="ri-search-line w-4 h-4 flex items-center justify-center"></i>
                        Consultar
                      </>
                    )}
                  </button>
                </div>
              </div>
              <p className="text-xs text-foreground-500">
                Ingresa el código que aparece en tu recibo de agua. Si no lo recuerdas, contáctanos por WhatsApp.
              </p>
            </form>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-center mb-6">
              <i className="ri-error-warning-line text-red-500 w-5 h-5 flex items-center justify-center mx-auto mb-1"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70 animate-slide-up">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-check-line text-green-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-foreground-950">Resultado de Consulta</h2>
                  <p className="text-xs text-foreground-500">Código: {codigo}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center py-2.5 border-b border-background-200/50">
                  <span className="text-sm text-foreground-600">Titular</span>
                  <span className="text-sm font-medium text-foreground-950">{result.nombre}</span>
                </div>
                <div className="flex justify-between items-center py-2.5 border-b border-background-200/50">
                  <span className="text-sm text-foreground-600">Deuda total</span>
                  <span className="text-sm font-bold text-red-600">Bs. {result.deuda.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2.5 border-b border-background-200/50">
                  <span className="text-sm text-foreground-600">Meses adeudados</span>
                  <span className="text-sm font-medium text-foreground-950">{result.meses}</span>
                </div>
                <div className="flex justify-between items-center py-2.5 border-b border-background-200/50">
                  <span className="text-sm text-foreground-600">Último pago</span>
                  <span className="text-sm font-medium text-foreground-950">{result.ultimoPago}</span>
                </div>
                <div className="flex justify-between items-center py-2.5 border-b border-background-200/50">
                  <span className="text-sm text-foreground-600">Lectura actual</span>
                  <span className="text-sm font-medium text-foreground-950">{result.lecturaActual} m³</span>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href="https://wa.me/59173455233"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors text-sm text-center flex items-center justify-center gap-2"
                >
                  <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center"></i>
                  Consultar por WhatsApp
                </a>
                <a
                  href="/pagar"
                  className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors text-sm text-center flex items-center justify-center gap-2"
                >
                  <i className="ri-qr-code-line w-4 h-4 flex items-center justify-center"></i>
                  Realizar Pago
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}